import { CartItems } from '@/hooks/CartContext';
import type { ComboRequirementGroup } from './comboCartUtils';
import {
    buildComboAllocationId,
    buildComboInstanceAllocationsMap,
    getComboInstanceStatuses,
    parseComboInstanceId,
} from './comboCartInstances';

export type AddProductToComboResult = {
    comboId: string;
    comboInstanceId: string;
    allocationId: string;
    cartItemId: string;
    groupId: string;
    createdNewInstance: boolean;
};

export type PendingScrollTarget = {
    comboId: string;
    comboInstanceId: string;
    allocationId?: string;
    cartItemId: string;
    reason: 'product-added' | 'new-instance-created';
};

export const buildComboAllocationDomId = (params: {
    allocationId?: string;
    comboId: string;
    comboInstanceId: string;
    groupId: string;
    cartItemId: string;
}): string => {
    if (params.allocationId) {
        return `combo-allocation-${params.allocationId}`;
    }

    return `combo-allocation-${[
        params.comboId,
        params.comboInstanceId,
        params.groupId,
        params.cartItemId,
    ].join('-')}`;
};

export const buildComboInstanceDomId = (comboInstanceId: string): string =>
    `combo-instance-${comboInstanceId}`;

const getAllocationKey = (
    cartItemId: string,
    entry: {
        allocationId?: string;
        id_campanha: string;
        comboInstanceId?: string;
        grupoId: string;
        cod_produto: number;
        qtd_embalagem: number;
    }
): string =>
    entry.allocationId ||
    buildComboAllocationId({
        cartItemId,
        comboId: entry.id_campanha,
        comboInstanceId: entry.comboInstanceId || '',
        groupId: entry.grupoId,
        cod_produto: entry.cod_produto,
        qtd_embalagem: entry.qtd_embalagem,
    });

const aggregateAllocationQuantities = (
    cartItemId: string,
    allocations: Array<{
        allocationId?: string;
        id_campanha: string;
        comboInstanceId?: string;
        grupoId: string;
        allocatedPackaging: number;
        cod_produto: number;
        qtd_embalagem: number;
    }>
) => {
    const map = new Map<
        string,
        {
            quantity: number;
            entry: (typeof allocations)[number];
        }
    >();

    allocations.forEach((entry) => {
        if (entry.allocatedPackaging <= 0 || !entry.comboInstanceId) return;

        const key = getAllocationKey(cartItemId, entry);
        const existing = map.get(key);

        if (existing) {
            existing.quantity += entry.allocatedPackaging;
            return;
        }

        map.set(key, { quantity: entry.allocatedPackaging, entry });
    });

    return map;
};

const simulateNextCartItems = (
    previousCartItems: CartItems[],
    params: {
        uid: string;
        sku: number;
        quantity: number;
        option_uid: string;
        category_id: number;
    }
): CartItems[] => {
    const nextItems = [...previousCartItems];
    const existingIndex = nextItems.findIndex(
        (item) => item.uid === params.uid && item.sku === params.sku
    );

    if (existingIndex >= 0) {
        nextItems[existingIndex] = {
            ...nextItems[existingIndex],
            quantity: params.quantity,
        };
        return nextItems;
    }

    return [
        ...nextItems,
        {
            uid: params.uid,
            sku: params.sku,
            quantity: params.quantity,
            option_uid: params.option_uid,
            category_id: params.category_id,
            addedAt: new Date().toISOString(),
        },
    ];
};

/**
 * Compara alocações antes/depois e retorna a instância/alocação que recebeu as novas unidades.
 */
export const resolveComboAllocationChange = (
    previousCartItems: CartItems[],
    nextCartItems: CartItems[],
    cartItemId: string,
    combos: Array<{
        id_campanha: string;
        grupos_exigencia?: ComboRequirementGroup[];
        regras_beneficio?: unknown;
    }>
): AddProductToComboResult | null => {
    if (!combos.length) return null;

    const lineBefore = previousCartItems.find((item) => item.uid === cartItemId);
    const lineAfter = nextCartItems.find((item) => item.uid === cartItemId);

    if (!lineAfter) return null;

    const previousQuantity = lineBefore?.quantity ?? 0;
    if (lineAfter.quantity <= previousQuantity) return null;

    const beforeMap = buildComboInstanceAllocationsMap(previousCartItems, combos);
    const afterMap = buildComboInstanceAllocationsMap(nextCartItems, combos);

    const beforeAllocations = beforeMap[cartItemId] || [];
    const afterAllocations = afterMap[cartItemId] || [];

    if (!afterAllocations.length) return null;

    const beforeByKey = aggregateAllocationQuantities(cartItemId, beforeAllocations);
    const afterByKey = aggregateAllocationQuantities(cartItemId, afterAllocations);
    const beforeInstanceIds = new Set(
        beforeAllocations
            .filter((entry) => entry.comboInstanceId && entry.allocatedPackaging > 0)
            .map((entry) => entry.comboInstanceId as string)
    );

    let bestResult: AddProductToComboResult | null = null;
    let bestKitIndex = -1;

    afterByKey.forEach(({ quantity, entry }, key) => {
        const previousQuantityForKey = beforeByKey.get(key)?.quantity ?? 0;
        if (quantity <= previousQuantityForKey || !entry.comboInstanceId) return;

        const kitIndex = parseComboInstanceId(entry.comboInstanceId)?.kitIndex ?? 0;
        const createdNewInstance = !beforeInstanceIds.has(entry.comboInstanceId);

        if (kitIndex < bestKitIndex) return;

        bestKitIndex = kitIndex;
        bestResult = {
            comboId: entry.id_campanha,
            comboInstanceId: entry.comboInstanceId,
            allocationId: key,
            cartItemId,
            groupId: entry.grupoId,
            createdNewInstance,
        };
    });

    return bestResult;
};

export const buildPendingScrollTargetFromResult = (
    result: AddProductToComboResult
): PendingScrollTarget => ({
    comboId: result.comboId,
    comboInstanceId: result.comboInstanceId,
    allocationId: result.allocationId,
    cartItemId: result.cartItemId,
    reason: result.createdNewInstance ? 'new-instance-created' : 'product-added',
});

export const resolveComboAllocationChangeForAdd = (
    previousCartItems: CartItems[],
    params: {
        uid: string;
        sku: number;
        quantity: number;
        option_uid: string;
        category_id: number;
    },
    combos: Array<{
        id_campanha: string;
        grupos_exigencia?: ComboRequirementGroup[];
        regras_beneficio?: unknown;
    }>
): AddProductToComboResult | null =>
    resolveComboAllocationChange(
        previousCartItems,
        simulateNextCartItems(previousCartItems, params),
        params.uid,
        combos
    );

export const resolveComboAllocationChangeForUpdate = (
    previousCartItems: CartItems[],
    params: { uid: string; quantity: number },
    combos: Array<{
        id_campanha: string;
        grupos_exigencia?: ComboRequirementGroup[];
        regras_beneficio?: unknown;
    }>
): AddProductToComboResult | null => {
    const nextItems = previousCartItems.map((item) =>
        item.uid === params.uid ? { ...item, quantity: params.quantity } : item
    );

    return resolveComboAllocationChange(previousCartItems, nextItems, params.uid, combos);
};

export const shouldScheduleComboScroll = (
    result: AddProductToComboResult,
    previousCartItems: CartItems[],
    combos: Array<{
        id_campanha: string;
        grupos_exigencia?: ComboRequirementGroup[];
        regras_beneficio?: unknown;
    }>
): boolean => {
    if (result.createdNewInstance) return true;

    const beforeMap = buildComboInstanceAllocationsMap(previousCartItems, combos);
    const beforeAllocations = beforeMap[result.cartItemId] || [];
    const beforeInstanceIds = Array.from(
        new Set(
            beforeAllocations
                .filter((entry) => entry.comboInstanceId && entry.allocatedPackaging > 0)
                .map((entry) => entry.comboInstanceId as string)
        )
    );

    const hadCompleteInstance = beforeInstanceIds.some((comboInstanceId) => {
        const idCampanha =
            beforeAllocations.find((entry) => entry.comboInstanceId === comboInstanceId)?.id_campanha ||
            result.comboId;
        const combo = combos.find((entry) => entry.id_campanha === idCampanha);
        if (!combo) return false;

        const statuses = getComboInstanceStatuses(combo, previousCartItems, (uid) => beforeMap[uid]);
        return statuses.find((status) => status.comboInstanceId === comboInstanceId)?.isComplete;
    });

    if (hadCompleteInstance) return true;

    const targetWasOpenBefore = beforeInstanceIds.includes(result.comboInstanceId);
    if (targetWasOpenBefore) return true;

    return false;
};
